// Builds a unified search index spanning every browsable destination:
//   - Articles (live from API)
//   - Glossary terms (static lib)
//   - "X vs Y" comparison pages (static lib)
//   - State + city insurance pages (static lib)
//   - Roundup "Best of" pages (static lib)
//   - Tools / calculators / static hubs (hardcoded)
//
// Each entry conforms to:
//   { type, title, subtitle, path, keywords }
// where `type` is one of: article | glossary | compare | state | city | roundup | tool
//
// Articles are async-fetched lazily (with sessionStorage cache) so the modal
// can open instantly using the static slice, then enrich once fetch resolves.

import { GLOSSARY } from "./glossary";
import { STATES } from "./states";
import { CITIES } from "./cities";
import { COMPARISONS } from "./comparisons";
import { ROUNDUPS } from "./roundups";
import { fetchArticles } from "./api";

const TOOLS = [
  { type: "tool", title: "Auto Loan Calculator", subtitle: "Monthly payment & total interest", path: "/auto-loan-calculator", keywords: "loan payment calculator monthly interest" },
  { type: "tool", title: "Refinance Calculator", subtitle: "See your APR savings vs. your current loan", path: "/auto-loan-refinance-calculator", keywords: "refinance refi savings apr lower rate" },
  { type: "tool", title: "Car Affordability Calculator", subtitle: "How much car you can actually afford", path: "/car-affordability-calculator", keywords: "affordability budget income dti debt to income" },
  { type: "tool", title: "Insurance Cost Estimator", subtitle: "2-minute premium estimate", path: "/auto-insurance-cost-estimator", keywords: "insurance premium estimator quote car policy" },
  { type: "tool", title: "All Calculators", subtitle: "Browse every CarSavr tool", path: "/calculators", keywords: "calculators tools" },
  { type: "tool", title: "Compare Hub", subtitle: "All side-by-side decision guides", path: "/compare", keywords: "compare versus side by side" },
  { type: "tool", title: "Best Of Roundups", subtitle: "Editorially-ranked lender + insurer lists", path: "/best", keywords: "best of top lenders insurers" },
  { type: "tool", title: "All Guides", subtitle: "Long-form articles & playbooks", path: "/guides", keywords: "guides articles long-form" },
];

const _staticIndex = [
  ...GLOSSARY.map((g) => ({
    type: "glossary",
    title: g.term,
    subtitle: g.short,
    path: `/glossary/${g.slug}`,
    keywords: `${g.term} ${g.short} ${g.body}`,
  })),
  ...COMPARISONS.map((c) => ({
    type: "compare",
    title: `${c.a.name} vs ${c.b.name}`,
    subtitle: c.description,
    path: `/${c.slug}`,
    keywords: `${c.a.name} ${c.b.name} ${c.category} ${c.description}`,
  })),
  ...STATES.map((s) => ({
    type: "state",
    title: `Auto insurance in ${s.name}`,
    subtitle: `Avg $${s.avg.toLocaleString()}/yr · State minimums ${s.min_bi} / ${s.min_pd}`,
    path: `/insurance/${s.slug}`,
    keywords: `${s.name} ${s.code} auto insurance ${s.notable}`,
  })),
  ...CITIES.map((c) => {
    const state = STATES.find((s) => s.slug === c.state);
    return {
      type: "city",
      title: `Auto insurance in ${c.name}`,
      subtitle: state ? `${state.name} · ${c.note.slice(0, 80)}…` : c.note,
      path: `/insurance/${c.state}/${c.slug}`,
      keywords: `${c.name} ${state?.name || ""} ${c.note}`,
    };
  }),
  ...ROUNDUPS.map((r) => ({
    type: "roundup",
    title: r.title,
    subtitle: r.description,
    path: `/${r.slug}`,
    keywords: `${r.title} ${r.audience} ${r.description}`,
  })),
  ...TOOLS,
];

const ARTICLE_CACHE_KEY = "carsavr_search_articles_v1";
const ARTICLE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function readCachedArticles() {
  try {
    const raw = sessionStorage.getItem(ARTICLE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > ARTICLE_TTL_MS) return null;
    return Array.isArray(parsed.items) ? parsed.items : null;
  } catch {
    return null;
  }
}

function writeCachedArticles(items) {
  try {
    sessionStorage.setItem(
      ARTICLE_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), items })
    );
  } catch {
    /* sessionStorage may be unavailable in some browsers */
  }
}

function articlesToIndex(arts) {
  return arts.map((a) => ({
    type: "article",
    title: a.title,
    subtitle: a.excerpt || a.category,
    path: `/guides/${a.slug}`,
    keywords: `${a.title} ${a.category} ${a.excerpt || ""}`,
  }));
}

// Returns the static slice immediately. Resolved promise carries the fully-
// hydrated index (static + articles).
export function getSearchIndex() {
  const cached = readCachedArticles();
  const baseStatic = _staticIndex;
  const immediate = cached ? [...baseStatic, ...articlesToIndex(cached)] : baseStatic;

  const promise = (async () => {
    try {
      const articles = await fetchArticles();
      const arts = Array.isArray(articles) ? articles : [];
      writeCachedArticles(arts);
      return [...baseStatic, ...articlesToIndex(arts)];
    } catch {
      return immediate;
    }
  })();

  return { immediate, promise };
}

export const TYPE_LABELS = {
  article: "Guide",
  glossary: "Glossary",
  compare: "Compare",
  state: "State",
  city: "City",
  roundup: "Best of",
  tool: "Tool",
};

export const TYPE_ORDER = [
  "article",
  "roundup",
  "tool",
  "compare",
  "glossary",
  "state",
  "city",
];
